/**
 * The /channels URL vocabulary: which section is open, and which political group is selected.
 *
 * Both halves are mapped explicitly rather than derived. A generic slugifier would be shorter and
 * would break the moment a group code contains a character that cannot sit in a path segment -
 * "S&D" carries an ampersand, "Greens/EFA" and "GUE/NGL" carry slashes, which would silently split
 * into extra segments and match nothing. Explicit also means a code the API renames cannot quietly
 * change a URL that someone has already bookmarked.
 *
 * The section slugs are the public wording, not the internal contributor types: "meps" reads as a
 * URL, "eppress" reads as a database column.
 */

/** Section slug in the URL -> the contributor `type` the API and the filters use. */
const SECTION_BY_SLUG: Readonly<Record<string, string>> = {
    "president": "president",
    "meps": "mep",
    "political-groups": "grouppress",
    "committees": "committee",
    "ep-accounts": "eppress",
    "other-institutions": "otherinstitution",
};

const SLUG_BY_SECTION: Readonly<Record<string, string>> = Object.fromEntries(
    Object.entries(SECTION_BY_SLUG).map(([slug, type]) => [type, slug]),
);

/** Group slug in the URL -> the `eParty` code the API returns. */
const EPARTY_BY_SLUG: Readonly<Record<string, string>> = {
    "epp": "EPP",
    "sd": "S&D",
    "renew": "Renew",
    "ecr": "ECR",
    "greens-efa": "Greens/EFA",
    "the-left": "The Left",
    "gue-ngl": "GUE/NGL",
    "pfe": "PfE",
    "esn": "ESN",
    "ni": "NI",
};

const SLUG_BY_EPARTY: Readonly<Record<string, string>> = Object.fromEntries(
    Object.entries(EPARTY_BY_SLUG).map(([slug, eparty]) => [eparty.toLowerCase(), slug]),
);

/** The section shown when the URL names none, and the group value meaning "no group filter". */
export const DEFAULT_SECTION = "all";
export const ALL_GROUPS = "All";

/**
 * A group the table does not know still needs a usable URL, or a new group would make the page
 * unreachable until someone edits this file. Lower-cased, separators folded to a hyphen, anything
 * else dropped - reversible only through the table, which is why it is a fallback and not the rule.
 */
const fallbackSlug = (eparty: string): string =>
    eparty.toLowerCase().replace(/[/&\s]+/g, "-").replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-");

export function sectionToSlug(type: string | undefined): string | undefined {
    if (! type || type === DEFAULT_SECTION) {
        return undefined;
    }
    return SLUG_BY_SECTION[type];
}

export function slugToSection(slug: string | undefined): string | undefined {
    if (! slug) {
        return DEFAULT_SECTION;
    }
    return SECTION_BY_SLUG[slug.toLowerCase()];
}

export function epartyToSlug(eparty: string | undefined): string | undefined {
    if (! eparty || eparty === ALL_GROUPS) {
        return undefined;
    }
    return SLUG_BY_EPARTY[eparty.toLowerCase()] ?? fallbackSlug(eparty);
}

export function slugToEparty(slug: string | undefined): string | undefined {
    if (! slug) {
        return ALL_GROUPS;
    }
    return EPARTY_BY_SLUG[slug.toLowerCase()];
}

/**
 * The canonical path for a section and group. No trailing slash, and the group is only ever part of
 * the URL where groups exist - only MEPs are grouped, so a group on any other section is dropped
 * rather than carried as a segment that filters nothing.
 */
export function buildChannelsPath(type: string | undefined, eparty?: string | undefined): string {
    const section = sectionToSlug(type);
    if (! section) {
        return "/channels";
    }
    const group = type === "mep" ? epartyToSlug(eparty) : undefined;
    return group ? `/channels/${section}/${group}` : `/channels/${section}`;
}
